import { useCallback, useState } from 'react';

import { isAxiosError } from 'axios';
import {
  BookText,
  Bot,
  MoreHorizontal,
  SquareArrowOutUpRight,
} from 'lucide-react';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore posthog is in the parent dir
import { usePostHog } from 'posthog-js/react';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore constants is in the parent dir
import DRUMKIT_AUTH_URL from '@constants/DrumkitAuthUrl';

import { Button } from 'components/Button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from 'components/Dropdown';
import { useAuth } from 'hooks/useAuth';
import { toast } from 'hooks/useToaster';
import { toggleQQAgentDisabled } from 'lib/api/agents/toggleQQAgentDisabled';
import { Maybe } from 'types/UtilityTypes';
import { QuickQuoteAgentPosthogEvents } from 'types/enums/CustomPosthogEvents';
import { cn } from 'utils/shadcn';

interface AgentActionsMenuProps {
  agentId: number;
  disabled: boolean;
  onDisabledChange: (agentId: number, disabled: boolean) => void;
}

export const AgentActionsMenu = ({
  agentId,
  disabled,
  onDisabledChange,
}: AgentActionsMenuProps) => {
  const { user } = useAuth();
  const posthog = usePostHog();
  const [isToggling, setIsToggling] = useState(false);

  const isDisabled = disabled;

  const buttonNamePosthog: Maybe<string> = isDisabled
    ? QuickQuoteAgentPosthogEvents.EnableQQAgent
    : QuickQuoteAgentPosthogEvents.DisableQQAgent;

  const handleToggleDisabled = useCallback(async () => {
    setIsToggling(true);
    try {
      const result = await toggleQQAgentDisabled(agentId, !isDisabled);

      if (result.isOk()) {
        // Capture PostHog event only after successful toggle
        if (buttonNamePosthog) {
          posthog?.capture(buttonNamePosthog, {
            quickQuoteAgentID: agentId,
            userID: user?.service_id,
            userEmailAddress: user?.email,
          });
        }

        onDisabledChange(agentId, result.value.disabled);
        toast({
          description: result.value.disabled
            ? 'Agent disabled. It will not run on new emails. You can view it or re-enable it from here or in Drumkit Portal.'
            : 'Agent enabled.',

          variant: result.value.disabled ? 'info' : 'success',
        });
      } else {
        toast({
          description: result.error.message,
          variant: 'destructive',
        });
      }
    } catch (error) {
      // Re-throw auth errors so the app can handle them (e.g. redirect to re-auth)
      if (isAxiosError(error) && error.response?.status === 401) {
        throw error;
      }
      toast({
        description: 'An unexpected error occurred. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsToggling(false);
    }
  }, [agentId, isDisabled, buttonNamePosthog, onDisabledChange, posthog, user]);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant='ghost'
          size='sm'
          className='h-6 w-6 p-0'
          buttonNamePosthog='agent-actions-menu'
        >
          <MoreHorizontal className='h-4 w-4' />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align='end'>
        <DropdownMenuItem
          onClick={() => window.open(`${DRUMKIT_AUTH_URL}/agents`, '_blank')}
          className='cursor-pointer'
        >
          <Bot className='mr-1.5 h-4 w-4' />
          View Agents
          <SquareArrowOutUpRight className='ml-1 h-3 w-3' />
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() =>
            window.open(`${DRUMKIT_AUTH_URL}/agents/history`, '_blank')
          }
          className='cursor-pointer'
        >
          <BookText className='mr-1.5 h-4 w-4' />
          View History
          <SquareArrowOutUpRight className='ml-1 h-3 w-3' />
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={handleToggleDisabled}
          disabled={isToggling}
          className={cn('cursor-pointer', isDisabled ? '' : 'text-error-500')}
        >
          {isToggling ? '...' : isDisabled ? 'Enable Agent' : 'Disable Agent'}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
